import moment, { Moment } from 'moment';

export type TViewMode = '12' | '24';

export const TOTAL_MINS = 24 * 60;
export const SNAP_MINS = 5;

/** Minutes elapsed since midnight for a moment (0..1439). */
export const minsOfDay = (m: Moment): number => m.hour() * 60 + m.minute();

/** Snap a minute value to the nearest SNAP_MINS increment. */
export const snap = (mins: number): number =>
  Math.round(mins / SNAP_MINS) * SNAP_MINS;

/** Clamp a minute value into the visible 0..(TOTAL-SNAP) window. */
export const clampMins = (mins: number): number =>
  Math.max(0, Math.min(TOTAL_MINS - SNAP_MINS, mins));

/** Return a clone of base with its time-of-day set to `mins` (seconds zeroed). */
export const withTimeOfDay = (base: Moment, mins: number): Moment =>
  base
    .clone()
    .hour(Math.floor(mins / 60))
    .minute(mins % 60)
    .second(0)
    .millisecond(0);

/** Format a minute-of-day value for display in the active view mode. */
export const formatTimeOfDay = (mins: number, mode: TViewMode): string => {
  const m = ((mins % TOTAL_MINS) + TOTAL_MINS) % TOTAL_MINS;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (mode === '24') {
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(min).padStart(2, '0')} ${suffix}`;
};

/** Human duration string, e.g. "1h 30m" / "45m" / "2h". Returns "0m" when <= 0. */
export const formatDuration = (totalMins: number): string => {
  if (totalMins <= 0) return '0m';
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Parse a free-typed time string into minutes-of-day. Accepts 24h ("14:05",
 * "1405", "9"), and 12h ("2:05 pm", "2pm"). Returns null when unparseable.
 */
export const parseTimeInput = (raw: string): number | null => {
  const value = raw.trim().toLowerCase();
  if (!value) return null;
  const formats = [
    'h:mm a',
    'h:mma',
    'h a',
    'ha',
    'H:mm',
    'HH:mm',
    'Hmm',
    'HHmm',
    'H',
    'HH',
  ];
  const parsed = moment(value, formats, true);
  if (!parsed.isValid()) return null;
  return parsed.hour() * 60 + parsed.minute();
};
